import { useState, useMemo } from "react";

export const useTableSort = (data, defaultSortKey = null, defaultSortOrder = "asc") => {
  const [sortConfig, setSortConfig] = useState(
    defaultSortKey ? { key: defaultSortKey, direction: defaultSortOrder } : null
  );

  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    let sortableItems = [...data];
    if (sortConfig !== null && sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Ensure null/undefined values go to the bottom
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Check if value is a date string
        const isDate = (val) => {
          if (typeof val === "string") {
            // Very basic heuristic for standard ISO dates or DD-MM-YYYY
            const dateRegex = /^\d{4}-\d{2}-\d{2}|^\d{2}-\d{2}-\d{4}|^\d{2}\/\d{2}\/\d{4}/;
            return dateRegex.test(val) && !isNaN(Date.parse(val));
          }
          return val instanceof Date;
        };

        if (isDate(aValue) && isDate(bValue)) {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else if (typeof aValue === "string" && typeof bValue === "string") {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === "asc" ? comparison : -comparison;
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        // Reset sort
        setSortConfig(null);
        return;
      }
    }
    setSortConfig({ key, direction });
  };

  return { sortedData, requestSort, sortConfig };
};
